/**
 * <sidebar-collapse-toggle>
 *
 * Toggles the cf2-sidebar-wrapper--collapsed class on the closest
 * .cf2-sidebar-wrapper ancestor. Persists state to localStorage so
 * the user's preference survives navigation. Falls back to the
 * data-default-state attribute when no localStorage value exists.
 */
class SidebarCollapseToggle extends HTMLElement {
  static STORAGE_KEY = 'cozy:cf2:sidebarState';

  connectedCallback() {
    this.button = this.querySelector('button');
    this.wrapper = this.closest('.cf2-sidebar-wrapper');
    if (!this.button || !this.wrapper) return;

    this.boundHandler = this._onClick.bind(this);
    this.button.addEventListener('click', this.boundHandler);
    this._restoreState();
  }

  disconnectedCallback() {
    if (this.button && this.boundHandler) {
      this.button.removeEventListener('click', this.boundHandler);
    }
  }

  _onClick() {
    const isCurrentlyCollapsed = this.wrapper.classList.contains('cf2-sidebar-wrapper--collapsed');
    const nextState = isCurrentlyCollapsed ? 'open' : 'collapsed';
    this._applyState(nextState);
    try {
      localStorage.setItem(SidebarCollapseToggle.STORAGE_KEY, nextState);
    } catch (e) {
      // localStorage may be disabled in private browsing — silently ignore
    }
  }

  _restoreState() {
    let state;
    try {
      state = localStorage.getItem(SidebarCollapseToggle.STORAGE_KEY);
    } catch (e) {
      state = null;
    }
    if (!state) {
      state = this.wrapper.dataset.defaultState || 'open';
    }
    this._applyState(state);
  }

  _applyState(state) {
    const isCollapsed = state === 'collapsed';
    this.wrapper.classList.toggle('cf2-sidebar-wrapper--collapsed', isCollapsed);
    this.button.setAttribute('aria-expanded', isCollapsed ? 'false' : 'true');
    const expandLabel = window.themeVariables?.strings?.collection?.expand_sidebar || 'Expand filters';
    const collapseLabel = window.themeVariables?.strings?.collection?.collapse_sidebar || 'Collapse filters';
    this.button.setAttribute('aria-label', isCollapsed ? expandLabel : collapseLabel);
  }
}

if (!window.customElements.get('sidebar-collapse-toggle')) {
  window.customElements.define('sidebar-collapse-toggle', SidebarCollapseToggle);
}
